import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Log, User, UserManager } from 'oidc-client';
import { StatusCodes } from 'http-status-codes';

import { AuthenticationTypes, USER_ROLES } from '../constants';
import { defaultApi } from '../apis';

import { CLIENT_CONFIG, trimBasePath, getCurrentPath, getCurrentUrl, AUTH_TYPE, GAMMA_LOGIN_PATH } from './configUtil';
import { browserHistory } from './historyUtil';

const REQUIRED_ROLES = [USER_ROLES.owner, USER_ROLES.admin, USER_ROLES.botsAdmin, USER_ROLES.usersAdmin];
const AUTH_ERROR_STATUSES = [StatusCodes.UNAUTHORIZED, StatusCodes.FORBIDDEN];
const CALLBACK_PREFIX = getCurrentPath('/auth/callback');
const LOGGED_OUT_PREFIX = getCurrentPath('/auth/logged-out');
const ERROR_PREFIX = getCurrentPath('/errors');

const USE_DUMMY_AUTH = AUTH_TYPE === AuthenticationTypes.DUMMY;
const USE_ELMA365_AUTH = AUTH_TYPE === AuthenticationTypes.ELMA365;
const CURRENT_TENANT_ID: string = USE_DUMMY_AUTH
  ? CLIENT_CONFIG.authentication.dummy.tenant_id
  : USE_ELMA365_AUTH
  ? CLIENT_CONFIG.authentication.elma365.tenant_id
  : '';

const DUMMY_USER: User = {
  access_token: '',
  expired: false,
  expires_at: Date.now() + 7 * 1440 * 60,
  expires_in: 7 * 1440 * 60,
  id_token: '',
  scope: '',
  scopes: [],
  state: '',
  token_type: 'jwt',
  profile: {
    'elma.assistant.claims.tenant_id': CURRENT_TENANT_ID,
    aud: '',
    exp: Date.now() + 7 * 1440 * 60,
    iat: Date.now(),
    iss: '',
    sub: '507f1f77bcf86cd799439010', // Unknown user id
    role: 'admin',
    name: 'Unknown',
  },
  toStorageString: () => '',
};

export const AGENTS_DEFAULT_PATH = '/agents';

declare global {
  interface Window {
    EB?: {
      getProfileAsync: () => Promise<IUserProfile>;
    };
  }
}

let currentManager: UserManager | null = null;
let currentState: string | null = null;
let currentUser: User | null = null;
let currentReloading = false;

Log.logger = console;

export interface IUserProfile {
  userId: string;
  userName: string;
  email: string;
  tenantId: string;
  originalHost?: string;
  accessToken: string;
  roles: string[];
}

interface IAuthArgs {
  state?: string;
}

const isReloading = (): boolean => {
  return currentReloading;
};

const getManager = async (): Promise<UserManager> => {
  let manager = currentManager;

  if (manager) {
    return manager;
  }

  const oidc = CLIENT_CONFIG.authentication.openIdConnect;
  const settings = {
    ...oidc,
    redirect_uri: oidc.redirect_uri || getCurrentUrl(CALLBACK_PREFIX),
    post_logout_redirect_uri: oidc.post_logout_redirect_uri || getCurrentUrl(LOGGED_OUT_PREFIX),
  };

  manager = new UserManager(settings);
  // eslint-disable-next-line require-atomic-updates
  currentManager = manager;
  return manager;
};

const getUser = (): User => {
  if (USE_DUMMY_AUTH && !currentUser) {
    currentUser = DUMMY_USER;
  }

  const user = currentUser;

  if (user) {
    return user;
  }

  throw new Error('User not logged in.');
};

const isAuthenticated = () => {
  if (USE_DUMMY_AUTH) {
    // eslint-disable-next-line no-console
    console.warn('Auth is disabled!');
    return true;
  }

  const user = currentUser;

  if (!user) {
    return false;
  }

  return !user.expired;
};

const getTenantId = (): string => {
  if (CURRENT_TENANT_ID) {
    return CURRENT_TENANT_ID;
  }

  const user = getUser();
  const tenantIdClaim = user.profile['elma.assistant.claims.tenant_id'];
  return Array.isArray(tenantIdClaim) ? tenantIdClaim[0] : tenantIdClaim;
};

const getProfileAsync = async (): Promise<IUserProfile> => {
  if (USE_ELMA365_AUTH) {
    try {
      const response = await defaultApi.getCurrentIdentity();
      const currentProvider = response.data.current;
      return {
        userId: currentProvider.subject_id,
        userName: currentProvider.name,
        email: currentProvider.email,
        tenantId: CURRENT_TENANT_ID,
        originalHost: '',
        accessToken: '',
        roles: ['admin'],
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  const user = getUser();
  const tenantIdClaim = user.profile['elma.assistant.claims.tenant_id'];
  const roleClaim = user.profile.role;

  return {
    userId: user.profile.sub,
    userName: user.profile.name || user.profile.sub,
    email: user.profile.email || '',
    tenantId: Array.isArray(tenantIdClaim) ? tenantIdClaim[0] : tenantIdClaim,
    originalHost: user.profile['elma.assistant.claims.original_host'],
    accessToken: user.access_token,
    roles: Array.isArray(roleClaim) ? roleClaim : [roleClaim],
  };
};
window.EB = {
  getProfileAsync,
};

const createArgs = (): IAuthArgs => {
  const { pathname, search, hash } = window.location;
  return { state: `${trimBasePath(pathname)}${search}${hash}` };
};

const loginUser = async (args?: IAuthArgs) => {
  currentReloading = true;
  args = args ?? createArgs();
  const manager = await getManager();
  await manager.signinRedirect(args);
};

const logoutUser = async (args?: IAuthArgs): Promise<void> => {
  currentReloading = true;
  args = args ?? createArgs();
  const manager = await getManager();
  await manager.signoutRedirect(args);
};

const clearSession = async () => {
  currentUser = null;
  const manager = await getManager();
  await manager.removeUser();
  await manager.clearStaleState();
};

const reloginUser = async (args?: IAuthArgs) => {
  await clearSession();
  await loginUser(args);
};

const isErrorRoute = (): boolean => {
  return window.location.pathname.startsWith(ERROR_PREFIX);
};

const isLoginCallback = () => {
  return window.location.pathname.startsWith(CALLBACK_PREFIX);
};

const isLogoutCallback = () => {
  return window.location.pathname.startsWith(LOGGED_OUT_PREFIX);
};

const withOfflineAccess = () => {
  return window.location.search.indexOf('offline_access') >= 0;
};

const isInvalidCallback = () => {
  return isLoginCallback() && !withOfflineAccess();
};

const isAuthCallback = () => {
  return isLoginCallback() || isLogoutCallback();
};

const processLoginCallback = async () => {
  const manager = await getManager();
  const user = await manager.signinRedirectCallback();
  currentState = user.state;
  currentUser = user;
};

const processLogoutCallback = async () => {
  const manager = await getManager();
  const response = await manager.signoutRedirectCallback();
  currentState = response.state;
  currentUser = null;
};

const processNormalRequest = async () => {
  if (USE_DUMMY_AUTH || USE_ELMA365_AUTH) {
    currentUser = DUMMY_USER;
    currentState = null;
    return;
  }

  const manager = await getManager();
  currentState = null;
  currentUser = await manager.getUser();
};

const hasRequiredRole = () => {
  const roleClaim = currentUser?.profile.role;
  const roles = Array.isArray(roleClaim) ? roleClaim : [roleClaim];
  return roles.some((role) => REQUIRED_ROLES.includes(role));
};

const updateBrowserHistory = () => {
  const state = currentState || AGENTS_DEFAULT_PATH;
  browserHistory.replace(state);
};

const setupManager = async () => {
  const oidc = CLIENT_CONFIG.authentication.openIdConnect;
  const manager = await getManager();

  manager.events.addUserLoaded((user: User) => {
    currentUser = user;
  });

  manager.events.addUserUnloaded(() => {
    currentUser = null;
  });

  if (oidc.reloginOnSessionExpiration) {
    manager.events.addUserSignedOut(async () => {
      await reloginUser();
    });
  }

  if (oidc.reloginOnSilentRenewError) {
    manager.events.addSilentRenewError(async () => {
      await reloginUser();
    });
  }
};

const processAuth = async (): Promise<boolean> => {
  if (isInvalidCallback()) {
    return false;
  }

  if (isLoginCallback()) {
    await processLoginCallback();
  } else if (isLogoutCallback()) {
    await processLogoutCallback();
  } else {
    await processNormalRequest();
  }

  if (isAuthCallback()) {
    updateBrowserHistory();
  }

  if (!isAuthenticated() && !USE_ELMA365_AUTH) {
    await loginUser();
    return false;
  }

  if (!hasRequiredRole()) {
    await logoutUser({});
    return false;
  }

  await setupManager();
  return true;
};

const setupAxios = (instance: AxiosInstance) => {
  const cancelSource = axios.CancelToken.source();

  instance.interceptors.request.use((options: AxiosRequestConfig) => {
    const token = currentUser?.access_token;
    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }
    return options;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      if (err.response && AUTH_ERROR_STATUSES.includes(err.response.status)) {
        cancelSource.cancel('Unauthorized');
        if (USE_ELMA365_AUTH) {
          window.location.href = `${GAMMA_LOGIN_PATH}?action=Return&return_to=${window.location.pathname}`;
        } else {
          await reloginUser();
        }
      }
      throw err;
    }
  );
};

const overrideAxios = () => {
  const create = axios.create;

  axios.create = function (config?: AxiosRequestConfig): AxiosInstance {
    const result = create.call(this, config);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (axios.interceptors.request as any).handlers.forEach((handler: any) => {
      result.interceptors.request.use(handler.fulfilled, handler.rejected);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (axios.interceptors.response as any).handlers.forEach((handler: any) => {
      result.interceptors.response.use(handler.fulfilled, handler.rejected);
    });

    return result;
  };
};

setupAxios(axios);
overrideAxios();

export { isReloading, isErrorRoute, getTenantId, getProfileAsync, logoutUser, processAuth };
